/**
 * Einen Servicefall abschließen – erst, wenn jeder Punkt erledigt ist.
 *
 * Der Abschluss fragt nach dem Ergebnis: Gewährleistung, Kulanz oder
 * kostenpflichtig. Kopf und alle Punkte landen in der Chronik.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer, Pflicht } from '../komponenten/Modal';
import type { AngebotAntwort, Servicefall } from '../typen';

const ERGEBNISSE = [
  { key: 'gewaehrleistung', label: 'Gewährleistung', text: 'Der Lieferant oder wir tragen es – kein Aufwand für den Kunden.' },
  { key: 'kulanz', label: 'Kulanz', text: 'Ohne Anspruch, aber wir übernehmen es – der Kunde soll zufrieden sein.' },
  { key: 'kostenpflichtig', label: 'kostenpflichtig', text: 'Wird dem Kunden in Rechnung gestellt – die Rechnung entsteht in sevDesk.' },
];

export function ServiceAbschlussModal({
  fall,
  onSchliessen,
  onFertig,
}: {
  fall: Servicefall;
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const { toastZeigen } = useKora();
  const [ergebnis, setErgebnis] = useState('');
  const [notiz, setNotiz] = useState('');
  const [fehler, setFehler] = useState('');

  const mutation = useMutation({
    mutationFn: () => api.sende<AngebotAntwort>(`/service/${fall.id}/abschliessen`, { ergebnis, notiz: notiz.trim() }),
    onSuccess: (a) => {
      onFertig();
      toastZeigen(a.toast ?? 'Servicefall abgeschlossen.');
      onSchliessen();
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Abschließen fehlgeschlagen.'),
  });

  return (
    <Modal
      titel="Servicefall abschließen"
      untertitel={`${fall.kennung} · ${fall.titel} · ${fall.gesamt} ${fall.gesamt === 1 ? 'Punkt' : 'Punkte'} erledigt`}
      breite={520}
      fest={mutation.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={mutation.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer ton="gruen" onClick={() => mutation.mutate()} disabled={mutation.isPending || !ergebnis}>
            {mutation.isPending ? 'Wird abgeschlossen …' : 'Abschließen'}
          </KnopfPrimaer>
        </>
      }
    >
      <div className="modal-feld">
        <span>Ergebnis<Pflicht /></span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {ERGEBNISSE.map((e) => (
            <button
              key={e.key}
              type="button"
              className={`modal-radio ${ergebnis === e.key ? 'aktiv' : ''}`}
              onClick={() => {
                setErgebnis(e.key);
                setFehler('');
              }}
            >
              <span className="modal-radio-punkt" />
              <span className="modal-radio-text">
                {e.label}
                <span style={{ color: 'var(--eh-text-3)', fontWeight: 400 }}> · {e.text}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
      <label className="modal-feld">
        <span>Notiz · optional</span>
        <textarea
          className="modal-textarea"
          rows={2}
          placeholder="Was zum Abschluss zu wissen ist"
          value={notiz}
          onChange={(e) => setNotiz(e.target.value)}
        />
      </label>
      <div className="modal-hinweis">
        Kopf und alle Punkte des Servicefalls landen in der Chronik. Der Auftrag verschwindet damit
        aus der Abwicklungsliste, sofern kein weiterer Punkt offen ist.
      </div>
      {fehler && <div className="modal-fehler">{fehler}</div>}
    </Modal>
  );
}
